import { DeckKey, TarotCardData } from '../types/tarot';

interface DailyReadingData {
  date: string;
  selectedCards: TarotCardData[];
  deckKey?: DeckKey;
  questionText?: string;
}

// Helper function to get current UTC date as string (YYYY-MM-DD)
const getCurrentUTCDateString = (): string => {
  const now = new Date();
  return now.getUTCFullYear() + '-' + 
         String(now.getUTCMonth() + 1).padStart(2, '0') + '-' + 
         String(now.getUTCDate()).padStart(2, '0');
};

const STORAGE_KEYS = {
  DAILY_READING: 'tarot_daily_reading',
  STREAK: 'tarot_streak',
} as const;

export const getDailyReading = (): DailyReadingData | null => {
  const stored = localStorage.getItem(STORAGE_KEYS.DAILY_READING);
  if (!stored) return null;
  
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const saveDailyReading = (cards: TarotCardData[], questionText?: string) => {
  const reading: DailyReadingData = {
    date: getCurrentUTCDateString(),
    selectedCards: cards,
    deckKey: cards[0]?.deckKey,
    questionText: questionText || undefined,
  };
  localStorage.setItem(STORAGE_KEYS.DAILY_READING, JSON.stringify(reading));
};

export const canDrawToday = (): boolean => {
  const reading = getDailyReading();
  if (!reading) return true;
  
  const todayUTC = getCurrentUTCDateString();
  const isToday = reading.date === todayUTC;
  
  // Clear old reading if it's from yesterday or earlier
  if (!isToday) {
    localStorage.removeItem(STORAGE_KEYS.DAILY_READING);
    console.log('🗑️ Cleared old reading from localStorage (date:', reading.date, 'today:', todayUTC, ')');
  }
  
  return !isToday;
};

export const getStreak = (): number => {
  const stored = localStorage.getItem(STORAGE_KEYS.STREAK);
  return stored ? parseInt(stored, 10) : 0;
};

// Get initial streak for new users - always starts at 0
export const getInitialStreak = (): number => {
  return 0;
};

export const updateStreak = () => {
  const reading = getDailyReading();
  if (!reading) return 0; // No reading = no streak

  const todayUTC = getCurrentUTCDateString();
  const yesterdayUTC = getYesterdayUTCDateString();

  let currentStreak = getStreak();

  if (reading.date === todayUTC) {
    // Today's reading is done - check if continuing streak
    const lastStreak = getStreak();
    if (lastStreak === 0) {
      // First reading ever or after break
      currentStreak = 1;
    } else {
      // Already had a streak, keep it (this handles multiple readings per day)
      currentStreak = lastStreak;
    }
  } else {
    // This shouldn't happen in normal flow, but handle it
    currentStreak = 1;
  }

  localStorage.setItem(STORAGE_KEYS.STREAK, currentStreak.toString());
  return currentStreak;
};

// Helper function to get yesterday's UTC date as string (YYYY-MM-DD)
const getYesterdayUTCDateString = (): string => {
  const yesterday = new Date();
  yesterday.setUTCDate(yesterday.getUTCDate() - 1);
  return yesterday.getUTCFullYear() + '-' + 
         String(yesterday.getUTCMonth() + 1).padStart(2, '0') + '-' + 
         String(yesterday.getUTCDate()).padStart(2, '0');
};

// Get days since last reading for display
export const getDaysSinceLastReading = (): number => {
  const reading = getDailyReading();
  if (!reading) return 0; // No reading = show 0
  
  const todayUTC = getCurrentUTCDateString();
  const readingDate = new Date(reading.date + 'T00:00:00Z');
  const today = new Date(todayUTC + 'T00:00:00Z');
  
  const diffTime = today.getTime() - readingDate.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
};

// Get days since last reading using Supabase date
export const getDaysSinceLastReadingFromDate = (lastReadingDate: string | null): number => {
  if (!lastReadingDate) return 0; // No reading = show 0
  
  const now = new Date();
  const lastReading = new Date(lastReadingDate);
  
  const diffTime = now.getTime() - lastReading.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffDays);
}; 
